import OTPVerification from '@components/common/OTPVerification';
import endpoints from '@config/endpoints';
import { errorLogger } from '@utils/applicationLogUtils';
import axios from '@utils/axiosExt';
import { decodeWithKeyAndShift, encodeWithKeyAndShift } from '@utils/base64Utils';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { postDataJSON } from '@services/CommonApiService';
import Button from '@components/common/Button';
import './index.css';
enum OTPRequestTypeCode {
    newOTP = "new_otp",
    resendOTP = "resend_otp",
    OTPTemplateCode = "mobile_number_verification_otp",
    OTPReferredFor = "Mobile Number Verification Flow"
}
interface ModalProps {
    showModal: boolean
    handleClose: () => void
    handleSubmit: (value: number) => void
    oldMobileNumber: string;
    showOnlyTextFieldModal: boolean;
}
const BeneficiaryMobileNumberUpdation: React.FC<ModalProps> = ({
    showModal,
    handleClose,
    handleSubmit,
    oldMobileNumber,
    showOnlyTextFieldModal = false
}) => {
    const [resetKey, setResetKey] = useState<number>(0);
    const [showSpinner, setSHowSpinner] = useState<boolean>(false);
    const [inputFieldShowSpinner, setInputFieldShowSpinner] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [retryMessage, setRetryMessage] = useState<string>('');
    const [retry, setRetry] = useState<number>(3);
    const [isTimeFinished, setIsTimeFinished] = useState<boolean>(false);
    const [otpExpiryTime, setOtpExpiryTime] = useState<number>(90);
    const [otpTransactionId, setOtpTransactionId] = useState<string>('');
    const [mobileNumber, setMobileNumber] = useState<string>('');
    const [textFieldIsError, setTextFieldIsError] = useState<boolean>(false);
    const [textFieldErrorMessage, setTextFieldErrorMessage] = useState<string>("");
    const [showMobileNumberAlreadyLinked, setShowMobileNumberAlreadyLinked] = useState<boolean>(false);
    const [showOTPComponent, setShowOTPComponent] = useState<boolean>(false);
    const onOTPSubmit = async (value: number) => {
        setSHowSpinner(true);
        const payload = {
            "otpTransactionId": otpTransactionId,
            "otp": value
        }
        const encodedData = encodeWithKeyAndShift(JSON.stringify(payload));
        try {
            const res = await axios.post(`${endpoints.verifyingOTP}`, { data: encodedData })
            const decodedData = JSON.parse(decodeWithKeyAndShift(res?.data?.data));
            if (res?.status === 200 && decodedData?.data[0].otpVerificationStatus === "success") {
                setSHowSpinner(false);
                handleSubmit(Number(mobileNumber));
                setShowError(false);
                toast.success("OTP verification successful.", { position: "top-center" });
            }
            else {
                setSHowSpinner(false);
                setShowError(true);
            }
        }
        catch (e: any) {
            setSHowSpinner(false);
            const decodedError = JSON.parse(decodeWithKeyAndShift(e?.response?.data?.data));
            setShowError(true);
            if (decodedError?.data[0].message === "invalid_otp") {
                setErrorMessage("Entered Incorrect OTP.")
            }
            else if (decodedError?.data[0].message === "timed_out") {
                setErrorMessage("OTP Expired. Please resend OTP.")
            }
            else if (decodedError?.data[0].message === "exceeded_max_retry_count") {
                setErrorMessage("Exceeded maximum no of OTP retries.")
            }
            else {
                setErrorMessage("Invalid OTP. Please resend OTP.")
            }
        }
    }
    const generateOTP = async (mobileNumber: string, otpRequestTypeCode: string, otpTransactionId: string) => {
        const payload = {
            otpTransactionId: otpTransactionId,
            otpRequestTypeCode: otpRequestTypeCode,
            mobileNumber: mobileNumber,
            otpTemplateCode: OTPRequestTypeCode.OTPTemplateCode,
            isSimulatorMode: false,
            referredFor: OTPRequestTypeCode.OTPReferredFor,
            operationType: "send_otp",
        };
        const encodedData = encodeWithKeyAndShift(JSON.stringify(payload));
        try {
            const res = await axios.post(`${endpoints.generateOTP}`, { data: encodedData })
            const decodedData = JSON.parse(decodeWithKeyAndShift(res?.data?.data));
            setSHowSpinner(false);
            if ((decodedData?.data[0]?.isSimulatorOn && decodedData?.data[0]?.otpTransactionId) || (decodedData?.data[0] && decodedData?.data[0]?.smsProviderStatusCode === "402")) {
                setOtpTransactionId(decodedData?.data[0].otpTransactionId);
                setOtpExpiryTime(decodedData?.data[0].otpExpiryTime);
                return true;
            } else if (decodedData?.data[0]?.smsProviderStatusCode === "406") {
                toast.error(`${decodedData?.data[0]?.smsProviderStatusCode}: ${decodedData?.data[0]?.errorMessage}`, { position: "top-center" });
                return false;
            } else {
                toast.error(`Unable to send OTP. Please try again later. Error Code: ${decodedData?.data[0]?.smsProviderStatusCode}`, { position: "top-center" });
                return false;
            }
        }
        catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error)
            setSHowSpinner(false);
            toast.error("Unable to send OTP. Please try again later.", { position: "top-center" });
            return false;
        }
    }
    const checkMobileNumberExists = async (mobileNumber: string) => {
        try {
            const mobData = {
                mobileNumber: mobileNumber
            }
            const encodedData = encodeWithKeyAndShift(JSON.stringify(mobData));
            const mobNumCheckResponse = await postDataJSON<any>(`${endpoints.checkBenefMobileNumberExists}`, { data: encodedData });
            const decodedResponse = JSON.parse(decodeWithKeyAndShift(mobNumCheckResponse.data.data));
            return decodedResponse.isBenefExist;
        }
        catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error)
            setInputFieldShowSpinner(false);
            toast.error("Error in verifying mobile number. Please try again.", { position: "top-center" });
            throw error;
        }
    }
    const generateOTPANDSubmit = async (value: string) => {
        setTextFieldIsError(false);
        setTextFieldErrorMessage("");
        const result = showOnlyTextFieldModal ? false : await generateOTP(value, OTPRequestTypeCode.newOTP, '');
        showOnlyTextFieldModal && handleSubmit(Number(value));
        return result;
    }
    const generateOTPForFirstTime = async (value: string) => {
        try {
            if (value === oldMobileNumber) {
                setTextFieldIsError(true);
                setTextFieldErrorMessage("New mobile number cannot be the same as the old one.");
                return false;
            }
            setInputFieldShowSpinner(true);
            const mobileNumberExists = await checkMobileNumberExists(value);
            setInputFieldShowSpinner(false);
            setMobileNumber(value);
            if (mobileNumberExists) {
                setShowMobileNumberAlreadyLinked(true);
                return false;
            }
            return await generateOTPANDSubmit(value);
        }
        catch (error) {
            setInputFieldShowSpinner(false);
            return false;
        }

    }
    const restartTimer = async () => {
        setShowError(false);
        if (retry <= 0) {
            handleClose();
            toast.error("You have reached the maximum limit to resend OTP. Please try again later.", { position: "top-center" });
        }
        else {
            setSHowSpinner(true);
            await generateOTP(mobileNumber, OTPRequestTypeCode.resendOTP, otpTransactionId);
            setResetKey(prevKey => prevKey + 1);
            setIsTimeFinished(false);
            setRetry(retry - 1);
            setRetryMessage('');
            setSHowSpinner(false);
        }
    }
    const handleOK = async () => {
        setShowMobileNumberAlreadyLinked(false);
        const result = await generateOTPANDSubmit(mobileNumber);
        result && setShowOTPComponent(true);
        return result;
    }
    const handleNo = () => {
        setShowMobileNumberAlreadyLinked(false);
    }
    const content =
        <div>
            <div style={{textAlign: "center"}}><span>Mobile number already linked to beneficiary. <br></br> 
                Proceed to update same number for this beneficiary?</span></div>
            <div className='beneficiary_update_mobile_number_yes_no'>
                <Button
                    onClick={handleOK}
                    disabled={false}
                    id="beneficiary_update_mobile_number_yes_no_buttons"
                    buttonName={'YES'}
                    colorType={'primary'}
                />
                <Button
                    onClick={handleNo}
                    disabled={false}
                    id="beneficiary_update_mobile_number_yes_no_buttons"
                    buttonName={'NO'}
                    colorType={'secondary'}
                />
            </div>

        </div>
        ;
    return (
        <>
            <OTPVerification
                showModal={showModal}
                handleClose={handleClose}
                inputFieldSpinner={inputFieldShowSpinner}
                handleInputFieldSubmit={generateOTPForFirstTime}
                labelNameForTextField={'Enter New Mobile Number'}
                lengthOfTextField={10}
                otpLabel={'An OTP has been sent to your mobile number ' + mobileNumber}
                onOTPSubmit={onOTPSubmit}
                otpLength={6}
                otpExpiryTime={otpExpiryTime}
                otpRestartTimer={restartTimer}
                otpResetKey={resetKey}
                otpShowSpinner={showSpinner}
                showError={showError}
                setShowError={setShowError}
                otpErrorMessage={errorMessage}
                otpRetryMessage={retryMessage}
                otpRetry={retry}
                isTimeFinished={isTimeFinished}
                setIsTimeFinished={setIsTimeFinished}
                textFieldErrorMessage={textFieldErrorMessage}
                textFieldIsError={textFieldIsError}
                setTextFieldIsError={setTextFieldIsError}
                showMobileNumberAlreadyLinked={showMobileNumberAlreadyLinked}
                setShowMobileNumberAlreadyLinked={setShowMobileNumberAlreadyLinked}
                contentToShow={content}
                defaultInputValue={mobileNumber}
                showOTPComponent={showOTPComponent}
                setShowOTPComponent={setShowOTPComponent}
            />
        </>
    );
};

export default BeneficiaryMobileNumberUpdation;
